import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { EventosService } from 'src/app/services/eventos.service';
import { CommonService } from 'src/app/services/common.service';
import { CiudadesService } from 'src/app/services/ciudades.service';
import { BehaviorSubject, flatMap, forkJoin, map, observable, Observable, of, shareReplay, Subscription } from 'rxjs';
import { CategoriasService } from 'src/app/services/categorias.service';
import { FilterService } from 'src/app/services/filter.service';
import { MatDialog } from '@angular/material/dialog';
import { StaticService } from 'src/app/services/static.service';
import { AuthenticationService } from 'src/app/services/authentication.service';
import { ActivatedRoute, Router } from '@angular/router';
import { InfoCardComponent } from '../shared/info-card/info-card.component';
import { AppSettings } from 'src/app/app-settings';
import { ChatbotComponent } from '../shared/chatbot/chatbot.component';
import { SessionsService } from 'src/app/services/sessions.service';
import { animate, style, transition, trigger } from '@angular/animations';

/** One show in the hero marquee. Every field comes from /public/events. */
export interface MarqueeShow {
  slug: string;
  /** Start of the next session; the template formats it with the es-ES locale */
  startsAt: Date;
  title: string;
  venue: string;
  price: number | null;
}

/** A bulb of the sign's arch, already placed on the ellipse. */
interface MarqueeBulb {
  left: number;
  top: string;
}

@Component({
  selector: 'app-home',
  templateUrl: './home.component.html',
  styleUrls: ['./home.component.scss'],
  animations: [
    // The city chips unfold out of the headline rather than dropping over it
    trigger('cityOptions', [
      transition(':enter', [
        style({ height: 0, opacity: 0, marginTop: 0 }),
        animate('260ms cubic-bezier(.2,.8,.3,1)',
          style({ height: '*', opacity: 1, marginTop: '*' }))
      ]),
      transition(':leave', [
        animate('180ms cubic-bezier(.4,0,.6,1)',
          style({ height: 0, opacity: 0, marginTop: 0 }))
      ])
    ])
  ]
})
export class HomeComponent implements OnInit, OnDestroy {

  explorar: Observable<any[]>;
  city!: any;
  ciudades: any[];
  eventosCategoriasHome: any[] = [];
  profile!: any;
  cercanas: any[] = [];

  private tituloExplorar = new BehaviorSubject<string>('Explorar');
  public tituloObs = this.tituloExplorar.asObservable();

  // --- Marquee hero ---
  /** The chips that unfold under the headline when the city is tapped. */
  cityPickerOpen: boolean = false;
  reducedMotion: boolean = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  marqueeShows: MarqueeShow[] = [];
  bulbs: MarqueeBulb[] = [];

  private marqueeSub?: Subscription;

  /** How many shows the marquee carries before it repeats. */
  private static readonly MAX_MARQUEE_SHOWS = 12;

  private subscriptions: Subscription = new Subscription();

  constructor(private eventosService: EventosService, private commonService: CommonService, private ciudadesService: CiudadesService,
    private categoriasService: CategoriasService, private filterService: FilterService, private dialog: MatDialog, private staticService: StaticService,
    private auth: AuthenticationService, private router: Router, private activatedRoute: ActivatedRoute, private sessionsService: SessionsService) {
    this.profile = this.auth.getProfile();
    let cityId = sessionStorage.getItem('city');
    this.commonService.setCity(cityId ? +cityId : null)
  }

  ngOnInit(): void {
    this.buildBulbs();
    localStorage.removeItem('aux');
    this.subscriptions.add(this.activatedRoute.queryParams.subscribe(
      (data) => {
        if (data.email && data.token) {
          this.auth.validateEmail(data.email, data.token).subscribe({
            next: (data) => {
              this.dialog.open(InfoCardComponent, {
                panelClass: 'info-dialog',
                data: {
                  icon: false,
                  text: 'Cuenta verificada correctamente. Inicia sesión para continuar.',
                  btnCancel: false
                }
              });
            },
            error: (err) => {
              this.dialog.open(InfoCardComponent, {
                panelClass: 'info-dialog',
                data: {
                  icon: true,
                  text: '¡Algo ha fallado! Inténtelo de nuevo de más tarde.',
                  btnCancel: false
                }
              });
            }
          })
        }
      }
    ))
    this.subscriptions.add(this.commonService.cityStorage.subscribe(
      currentCity => {
        this.eventosCategoriasHome = [];
        if (currentCity && this.city && currentCity == this.city.id) {
          return;
        }

        if (currentCity) {
          this.ciudadesService.getCiudadbyId(currentCity).subscribe((res: any) => {
            this.city = res.data
            this.tituloExplorar.next('Explorar en ' + res?.data?.name);
          })
        } else {
          this.city = null;
          this.tituloExplorar.next('Explorar');
        }
        this.getData(currentCity);
      }
    ));
    this.ciudadesService.getCitiesWithEvents().subscribe(res => { this.ciudades = res.data.data; });
    this.filterService.resetAllFilters();
    localStorage.removeItem('search');
    this.commonService.setCategory(null);
  }

  ngOnDestroy(): void {
    this.marqueeSub?.unsubscribe();
    this.subscriptions.unsubscribe();
  }

  /** The arch is rebuilt on resize because the clip-path curve changes per breakpoint. */
  @HostListener('window:resize')
  onResize(): void {
    this.buildBulbs();
  }

  goToLink() {
    this.router.navigate(['/results']);
  }

  getData(ciudad: number) {
    // shareReplay: the template already consumes `explorar` through two `| async`
    // pipes and the marquee subscribes a third time. Without it, three requests.
    this.explorar = this.eventosService.getEventosExplorar(ciudad).pipe(
      map(eventos => {
        return eventos.data.data.filter(evento => {
          let nextSession = this.sessionsService.getNextSession(evento);
          const matchesCity = !ciudad || evento.id_city === ciudad;
          return evento.publish && !evento.draft && nextSession && matchesCity;
        });
      }),
      shareReplay({ bufferSize: 1, refCount: false })
    );

    this.marqueeSub?.unsubscribe();
    this.marqueeSub = this.explorar.subscribe({
      next: events => this.marqueeShows = this.buildMarqueeShows(events),
      error: () => this.marqueeShows = []
    });
  }

  /**
   * Places the bulbs along the hero's elliptical edge.
   *
   * The clip is ellipse(rx 100% at 50% 0%), that is
   * ((x - W/2) / (rx·W))² + (y / H)² = 1. Solving for y gives
   * y/H = √(1 - ((p - 0.5)/rx)²) with p = x/W, so the vertical position
   * as a percentage depends only on the horizontal one and holds for any
   * hero height. rx changes with the breakpoint, hence the rebuild on resize.
   */
  private buildBulbs(): void {
    const width = window.innerWidth;
    const rx = width <= 578 ? 1.5 : width <= 992 ? 1.3 : 1;
    const count = width <= 578 ? 14 : 27;
    // inset from the curve, proportional to the width like the rest of the hero
    const inset = Math.min(44, Math.max(16, Math.round(width * 0.022)));

    this.bulbs = Array.from({ length: count }, (_, i) => {
      const p = 0.035 + (0.93 * i) / (count - 1);
      const y = Math.sqrt(1 - Math.pow((p - 0.5) / rx, 2)) * 100;
      return { left: p * 100, top: `calc(${y.toFixed(3)}% - ${inset}px)` };
    });
  }

  private buildMarqueeShows(events: any[]): MarqueeShow[] {
    return (events || [])
      // private events still list below, gated by their code; the marquee
      // advertises them with a price as if anyone could buy
      .filter(event => !event.private_event)
      .map(event => {
        const session = this.sessionsService.getNextSession(event);
        if (!session) {
          return null;
        }
        const title = event.title || '';
        return {
          slug: event.id + '-' + title.replaceAll(' ', '-'),
          startsAt: new Date(session.start_date),
          title,
          venue: event.space?.name || '',
          price: event.custom_from_price ?? event.from_price ?? null
        } as MarqueeShow;
      })
      .filter((show): show is MarqueeShow => !!show)
      // events arrive sorted by start_date, but each one's next session
      // may fall in a different order
      .sort((a, b) => a.startsAt.getTime() - b.startsAt.getTime())
      .slice(0, HomeComponent.MAX_MARQUEE_SHOWS);
  }

  toggleCityPicker(): void {
    this.cityPickerOpen = !this.cityPickerOpen;
  }

  /** `null` means every city. */
  pickCity(city: any): void {
    // Deliberately not assigning this.city here. The cityStorage subscription
    // bails out early when currentCity already matches this.city.id, so
    // setting it first would swap the headline and leave the marquee and the
    // carousel on the previous city. That subscription owns this.city.
    this.commonService.setCity(city ? city.id : null);
    this.cityPickerOpen = false;
  }

  openChatbot() {
    const dialogRef = this.dialog.open(ChatbotComponent, {
      panelClass: ['dialog-chatbot', 'not-padding'],
      position: {
        bottom: '0px',
        right: '0px'
      }
    })
  }

}
